import React from "react";

const RECURRENCES = [
  { value: "Monthly", label: "Monthly" },
  { value: "Weekly", label: "Weekly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Yearly", label: "Yearly" },
  { value: "One-time", label: "One-time" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

// Given a stored YYYY-MM-DD, extract the day-of-month (or null)
export const dayFromDateString = (iso) => {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return "";
  const d = parseInt(parts[2], 10);
  return Number.isFinite(d) && d >= 1 && d <= 31 ? String(d) : "";
};

// Build a YYYY-MM-DD using today's year/month + given day-of-month
export const dateStringFromDay = (day) => {
  const d = parseInt(day, 10);
  if (!Number.isFinite(d) || d < 1 || d > 31) return "";
  const { year, month } = todayISO();
  return `${year}-${pad2(month)}-${pad2(d)}`;
};

/**
 * PaymentScheduleFields renders the "Payment Due Date + Recurrence" pair
 * with an optional "Same day every month" mode.
 *
 * Props:
 *  - dueDate:      current YYYY-MM-DD or ""
 *  - recurrence:   current recurrence string
 *  - sameDayMode:  boolean — true = day-of-month input, false = calendar date
 *  - dayOfMonth:   current day string ("1".."31") — used only in sameDayMode
 *  - onChange({ dueDate, recurrence, sameDayMode, dayOfMonth }) — partial updates
 *  - testIdPrefix: prefix for data-testid attributes
 */
const PaymentScheduleFields = ({
  dueDate,
  recurrence,
  sameDayMode,
  dayOfMonth,
  onChange,
  testIdPrefix = "account",
}) => {
  const isMonthly = recurrence === "Monthly";
  // When recurrence isn't Monthly, force specific-date mode (no "same day" concept)
  const effectiveSameDay = isMonthly && sameDayMode;

  const handleRecurrenceChange = (value) => {
    if (value !== "Monthly") {
      onChange({ recurrence: value, sameDayMode: false });
    } else {
      onChange({ recurrence: value });
    }
  };

  const handleSameDayToggle = (checked) => {
    if (checked) {
      // Switching to day-of-month: seed day from existing date if any
      const day = dayFromDateString(dueDate);
      onChange({ sameDayMode: true, dayOfMonth: day, dueDate: day ? dateStringFromDay(day) : "" });
    } else {
      onChange({ sameDayMode: false });
    }
  };

  const handleDayChange = (raw) => {
    const cleaned = raw.replace(/[^0-9]/g, "").slice(0, 2);
    const dueDateStr = cleaned ? dateStringFromDay(cleaned) : "";
    onChange({ dayOfMonth: cleaned, dueDate: dueDateStr });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">Payment Due Date (Optional)</label>

      {isMonthly && (
        <label className="mt-1 inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}-same-day-checkbox`}
            className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={effectiveSameDay}
            onChange={(e) => handleSameDayToggle(e.target.checked)}
          />
          <span className="text-xs text-gray-700">Same day every month</span>
        </label>
      )}

      <div className="mt-1 grid grid-cols-2 gap-3">
        {effectiveSameDay ? (
          <div className="relative">
            <input
              type="number"
              min="1"
              max="31"
              placeholder="Day"
              data-testid={`${testIdPrefix}-day-of-month-input`}
              className="block w-full border border-gray-300 rounded-md pl-3 pr-16 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={dayOfMonth || ""}
              onChange={(e) => handleDayChange(e.target.value)}
            />
            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">
              of month
            </span>
          </div>
        ) : (
          <input
            type="date"
            data-testid={`${testIdPrefix}-due-date-input`}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={dueDate}
            onChange={(e) => onChange({ dueDate: e.target.value })}
          />
        )}
        <select
          data-testid={`${testIdPrefix}-recurrence-select`}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={recurrence}
          onChange={(e) => handleRecurrenceChange(e.target.value)}
        >
          {RECURRENCES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {effectiveSameDay
          ? "The due date will roll forward automatically each month."
          : "Set the next due date and how often it repeats."}
      </p>
    </div>
  );
};

export default PaymentScheduleFields;
